import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {CalendarProject} from 'calendar/CalendarConstants';
import {Member} from 'orgs/OrganizationConstants';
import {GRAY, BLUE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';

import CalendarActions from './CalendarActions';
import CalendarUtil from './CalendarUtil';

const INITIAL_MAX_MEMBERS_TO_SHOW = 3;

interface Props {
  hiddenProjectIds: Array<string>;
  hiddenMemberIds: Array<string>;
  projects: Array<CalendarProject>;
  members: Array<Member> | null;
}

interface State {
  maxMembersToShow: number;
}

class CalendarSidebarFilter extends React.Component<Props, State> {
  override state: State = {
    maxMembersToShow: INITIAL_MAX_MEMBERS_TO_SHOW,
  };

  _handleSeeMoreMembers = (): void => {
    this.setState({
      maxMembersToShow: this.props.members!.length,
    });
  };

  _handleSeeLessMembers = (): void => {
    this.setState({
      maxMembersToShow: INITIAL_MAX_MEMBERS_TO_SHOW,
    });
  };

  _handleAllMembersToggle = (): void => {
    const shouldHideMembers = this.props.hiddenMemberIds.length === 0;
    const memberIds = this.props.members!.map((member) => member.id);
    CalendarActions.updateFilteredMembers(memberIds, shouldHideMembers);
  };

  _handleMemberToggle = (memberId: string, shouldHide: boolean): void => {
    CalendarActions.updateFilteredMembers([memberId], shouldHide);
  };

  renderProjects = (): React.ReactNode => {
    const projects = _.sortBy(this.props.projects, (project) => project.name.toLowerCase());
    if (projects.length === 0) {
      return null;
    }

    const projectColorMapping = CalendarUtil.getProjectColorMapping(this.props.projects);

    return (
      <div className="calendarSidebarFilter-filterContainer">
        <div className="calendarSidebarFilter-title">Projects</div>
        {projects.map((project, i) => {
          const projectColor = projectColorMapping[project.id];
          const isProjectHidden = this.props.hiddenProjectIds.includes(project.id);
          return (
            <div key={i}>
              <div
                className="calendarSidebarFilter-option u-pointer"
                onClick={() => {
                  CalendarActions.toggleProjectHidden(project.id);
                }}
              >
                <span
                  className="calendarSidebarFilter-projectCheckbox u-mini-spacing-right"
                  style={{
                    backgroundColor: isProjectHidden ? undefined : projectColor,
                    border: isProjectHidden ? `solid 1px ${GRAY}` : undefined,
                  }}
                />
                <span className="legacy-font-proxima-11">{project.name}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderSeeMoreOrLessToggle = (): React.ReactNode => {
    if (this.props.members!.length > this.state.maxMembersToShow) {
      return (
        <a onClick={this._handleSeeMoreMembers}>
          <BenchlingIcon className="u-mini-spacing-right" icon="chevron-down" />
          See More
        </a>
      );
    } else if (this.state.maxMembersToShow > INITIAL_MAX_MEMBERS_TO_SHOW) {
      return (
        <a onClick={this._handleSeeLessMembers}>
          <BenchlingIcon className="u-mini-spacing-right" icon="chevron-up" />
          See Less
        </a>
      );
    }
    return null;
  };

  renderMembers = (): React.ReactNode => {
    if (!this.props.members || this.props.members.length === 0) {
      return null;
    }
    const members = _.sortBy(this.props.members, (member) => member.name.toLowerCase());
    const checkedMembers: {[memberId: string]: boolean} = _.object(
      members.map((member) => [member.id, !this.props.hiddenMemberIds.includes(member.id)])
    );
    const isMemberSelected = (member: Member): boolean => checkedMembers[member.id];

    return (
      <div>
        <div className="calendarSidebarFilter-title">Members</div>
        <div>
          <label className="u-pointer">
            <IndeterminateCheckbox
              className="calendarSidebarFilter-checkbox"
              items={members}
              selectedMap={isMemberSelected}
              onChange={this._handleAllMembersToggle}
            />
            <span>All Members</span>
          </label>
        </div>
        {members.slice(0, this.state.maxMembersToShow).map((member) => (
          <div key={member.id}>
            <label className="u-pointer">
              <input
                className={classNames('calendarSidebarFilter-checkbox', css(styles.checkbox))}
                type="checkbox"
                checked={checkedMembers[member.id]}
                onChange={() => {
                  this._handleMemberToggle(member.id, checkedMembers[member.id]);
                }}
              />
              <span>{member.name}</span>
            </label>
          </div>
        ))}
        {this.renderSeeMoreOrLessToggle()}
      </div>
    );
  };

  override render(): JSX.Element {
    return (
      <div className="calendarSidebarFilter">
        {this.renderProjects()}
        {this.renderMembers()}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default CalendarSidebarFilter;
